import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSetAtom } from 'jotai'
import { Icon } from '@equinor/eds-core-react'

import Loading from '@/components/loading'
import { useWindowWidth } from '@/hooks/useWindowWidth'
import { useGetOnboardedSensors } from '@/core/hooks-and-services/onboardedSensorsService'
import { useCurrentPlant } from '@/core/hooks-and-services/useCurrentPlant'
import { usePlantStatusAccess } from '@/core/hooks-and-services/accessService'
import { InstalledSensorStatus, StatusDto, BatteryStatus, SignalQuality } from '@/core/models/types'
import { BATTERY_STATUS_OPTIONS, SIGNAL_QUALITY_OPTIONS, HealthFilterOption } from '@/core/models/sensorHealth'
import { STATUS_COLORS } from '@/core/models/sensorStatusColors'
import {
    searchAtom,
    selectedFilterAreaAtom,
    selectedFilterBatteryStatusAtom,
    selectedFilterDeviceAtom,
    selectedFilterProjectAtom,
    selectedFilterProtocolAtom,
    selectedFilterSensorTypeAtom,
    selectedFilterSignalQualityAtom,
    selectedFilterStatusAtom,
    selectedFilterSuperiorTagAtom,
    selectedFilterSystemAtom,
} from '@/core/store/globalStore'

import { DonutSegment } from '../components/DonutChart'
import ChartCard from '../components/ChartCard'
import NoAccess from '../components/NoAccess'
import { aerialDrone } from '../icons'
import {
    CardGrid,
    CardHeader,
    CardSubtitle,
    CardTitle,
    DesktopMessage,
    LegendBadge,
    LegendItem,
    LegendLabel,
    Message,
    PageContainer,
    PageTitle,
    SensorTypeList,
    StatCard,
    StatTextWrapper,
    StatValue,
    WideCard,
} from '../styles'

const DESKTOP_BREAKPOINT = 1024

// Statuses shown in the "Status" donut, with the display name used by the
// Sensor overview filter so drilldown produces a matching filter chip.
const STATUS_META: {
    status: InstalledSensorStatus
    label: string
    filterLabel: string
    color: string
}[] = [
    {
        status: InstalledSensorStatus.Offline,
        label: 'Offline',
        filterLabel: 'Offline sensors',
        color: STATUS_COLORS[InstalledSensorStatus.Offline],
    },
    {
        status: InstalledSensorStatus.Unsuccessful,
        label: 'Unsuccessful',
        filterLabel: 'Unsuccessful sensors',
        color: STATUS_COLORS[InstalledSensorStatus.Unsuccessful],
    },
    {
        status: InstalledSensorStatus.Prepared,
        label: 'Prepared',
        filterLabel: 'Prepared sensors',
        color: STATUS_COLORS[InstalledSensorStatus.Prepared],
    },
    {
        status: InstalledSensorStatus.Draft,
        label: 'Draft',
        filterLabel: 'Drafted sensors',
        color: STATUS_COLORS[InstalledSensorStatus.Draft],
    },
    {
        status: InstalledSensorStatus.Active,
        label: 'Active',
        filterLabel: 'Active sensors',
        color: STATUS_COLORS[InstalledSensorStatus.Active],
    },
]

// Counts sensors per BatteryStatus / SignalQuality bucket using the same options
// (label + colour) that drive the Sensor overview filter, keeping the two 1:1.
const buildEnumSegments = <T extends string>(
    values: (T | null | undefined)[],
    options: HealthFilterOption<T>[],
    naValue: T
): DonutSegment[] => {
    const counts = new Map<T, number>()
    values.forEach((value) => {
        const key = value ?? naValue
        counts.set(key, (counts.get(key) ?? 0) + 1)
    })

    return options.map((option) => ({
        key: option.value,
        label: option.label,
        color: option.color,
        value: counts.get(option.value) ?? 0,
    }))
}

const PlantStatus = () => {
    const windowWidth = useWindowWidth()
    const navigate = useNavigate()

    const { data: sensors, isLoading: isLoadingSensors } = useGetOnboardedSensors()
    const { currentPlant, isLoading: isLoadingPlant } = useCurrentPlant()

    const setSelectedStatus = useSetAtom(selectedFilterStatusAtom)
    const setSelectedDevice = useSetAtom(selectedFilterDeviceAtom)
    const setSelectedProtocol = useSetAtom(selectedFilterProtocolAtom)
    const setSelectedProject = useSetAtom(selectedFilterProjectAtom)
    const setSelectedSystem = useSetAtom(selectedFilterSystemAtom)
    const setSelectedArea = useSetAtom(selectedFilterAreaAtom)
    const setSelectedSuperiorTag = useSetAtom(selectedFilterSuperiorTagAtom)
    const setSelectedSensorType = useSetAtom(selectedFilterSensorTypeAtom)
    const setSelectedBatteryStatus = useSetAtom(selectedFilterBatteryStatusAtom)
    const setSelectedSignalQuality = useSetAtom(selectedFilterSignalQualityAtom)
    const setSearch = useSetAtom(searchAtom)

    const plantShortName = currentPlant?.shortName ?? ''
    const isAllPlants = plantShortName === ''

    // The access decision (all plants => admin, specific plant => plant access) is
    // computed by the backend; the page only reflects it.
    const { data: hasAccess = false, isPending: isAccessPending } = usePlantStatusAccess(
        plantShortName,
        !isLoadingPlant
    )

    const plantSensors = useMemo(
        () => (sensors ?? []).filter((sensor) => isAllPlants || sensor.plant === plantShortName),
        [sensors, plantShortName, isAllPlants]
    )

    const countByStatus = useMemo(() => {
        const counts = new Map<InstalledSensorStatus, number>()
        plantSensors.forEach((sensor) => {
            if (!sensor.status) return
            counts.set(sensor.status, (counts.get(sensor.status) ?? 0) + 1)
        })
        return counts
    }, [plantSensors])

    // Total sensors installed, battery/signal diagrams, sensor-type counts only ACTIVE and OFFLINE sensors.
    const monitoredSensors = useMemo(
        () =>
            plantSensors.filter(
                (sensor) =>
                    sensor.status === InstalledSensorStatus.Active || sensor.status === InstalledSensorStatus.Offline
            ),
        [plantSensors]
    )

    // Count sensors per type for this plant; only types actually present are shown.
    const sensorTypeCounts = (() => {
        const counts = new Map<string, number>()
        monitoredSensors.forEach((sensor) => {
            const type = sensor.sensorType
            if (!type) return
            counts.set(type, (counts.get(type) ?? 0) + 1)
        })
        return Array.from(counts.entries())
            .map(([type, count]) => ({ type, count }))
            .sort((a, b) => a.type.localeCompare(b.type))
    })()

    const statusSegments: DonutSegment[] = STATUS_META.map((meta) => ({
        key: meta.status,
        label: meta.label,
        color: meta.color,
        value: countByStatus.get(meta.status) ?? 0,
    }))

    const batterySegments = buildEnumSegments(
        monitoredSensors.map((sensor) => sensor.batteryStatus),
        BATTERY_STATUS_OPTIONS,
        BatteryStatus.NotAvailable
    )

    const signalSegments = buildEnumSegments(
        monitoredSensors.map((sensor) => sensor.signalQuality),
        SIGNAL_QUALITY_OPTIONS,
        SignalQuality.NotAvailable
    )

    const totalSensors = monitoredSensors.length

    const monitoredStatusFilters: StatusDto[] = STATUS_META.filter(
        (meta) => meta.status === InstalledSensorStatus.Active || meta.status === InstalledSensorStatus.Offline
    ).map((meta) => ({ displayName: meta.filterLabel, value: meta.status }))

    const resetDrilldownFilters = () => {
        setSelectedStatus([])
        setSelectedDevice(null)
        setSelectedProtocol('')
        setSelectedProject(null)
        setSelectedSystem(null)
        setSelectedArea(null)
        setSelectedSuperiorTag(null)
        setSelectedSensorType(null)
        setSelectedBatteryStatus(null)
        setSelectedSignalQuality(null)
        setSearch(null)
    }

    const drilldownToSensorOverview = (status: InstalledSensorStatus, filterLabel: string) => {
        const statusFilter: StatusDto = { displayName: filterLabel, value: status }
        resetDrilldownFilters()
        setSelectedStatus([statusFilter])
        navigate('/dashboard')
    }

    const handleStatusSegmentClick = (segment: DonutSegment) => {
        const meta = STATUS_META.find((current) => current.status === segment.key)
        if (meta) drilldownToSensorOverview(meta.status, meta.filterLabel)
    }

    const handleSensorTypeClick = (type: string) => {
        resetDrilldownFilters()
        setSelectedStatus(monitoredStatusFilters)
        setSelectedSensorType(type)
        navigate('/dashboard')
    }

    const handleTotalClick = () => {
        resetDrilldownFilters()
        setSelectedStatus(monitoredStatusFilters)
        navigate('/dashboard')
    }

    const handleBatterySegmentClick = (segment: DonutSegment) => {
        const option = BATTERY_STATUS_OPTIONS.find((current) => current.value === segment.key)
        if (!option) return
        resetDrilldownFilters()
        setSelectedStatus(monitoredStatusFilters)
        setSelectedBatteryStatus(option.value)
        navigate('/dashboard')
    }

    const handleSignalSegmentClick = (segment: DonutSegment) => {
        const option = SIGNAL_QUALITY_OPTIONS.find((current) => current.value === segment.key)
        if (!option) return
        resetDrilldownFilters()
        setSelectedStatus(monitoredStatusFilters)
        setSelectedSignalQuality(option.value)
        navigate('/dashboard')
    }

    if (windowWidth <= DESKTOP_BREAKPOINT) {
        return (
            <PageContainer>
                <DesktopMessage variant="h6">Plant status is only available on desktop.</DesktopMessage>
            </PageContainer>
        )
    }

    if (isLoadingPlant || isLoadingSensors || isAccessPending) {
        return (
            <PageContainer>
                <Loading />
            </PageContainer>
        )
    }

    if (!hasAccess) {
        // Plant descriptions can carry a trailing " - <plantId>"; the access request
        // needs the plain plant name (e.g. "Kårstø").
        const plantName = (currentPlant?.description ?? plantShortName).replace(/\s*-\s*\d+\s*$/, '')
        return <NoAccess plantName={isAllPlants ? undefined : plantName} />
    }

    return (
        <PageContainer>
            <PageTitle variant="h1">Plant status</PageTitle>

            <CardGrid>
                <StatCard>
                    <StatTextWrapper>
                        <CardHeader>
                            <CardTitle variant="body_short">Total sensors installed</CardTitle>
                            <CardSubtitle variant="overline">Active &amp; offline sensors</CardSubtitle>
                        </CardHeader>
                        <StatValue type="button" onClick={handleTotalClick}>
                            {totalSensors}
                        </StatValue>
                    </StatTextWrapper>
                    <Icon data={aerialDrone} size={40} color="#3d3d3d" />
                </StatCard>

                <WideCard>
                    <CardHeader>
                        <CardTitle variant="body_short">Sensor types</CardTitle>
                        <CardSubtitle variant="overline">Active &amp; offline sensors</CardSubtitle>
                    </CardHeader>
                    {sensorTypeCounts.length > 0 ? (
                        <SensorTypeList>
                            {sensorTypeCounts.map(({ type, count }) => (
                                <LegendItem
                                    key={type}
                                    type="button"
                                    $clickable
                                    onClick={() => handleSensorTypeClick(type)}
                                >
                                    <LegendBadge $color="#b5daf5">{count}</LegendBadge>
                                    <LegendLabel>{type}</LegendLabel>
                                </LegendItem>
                            ))}
                        </SensorTypeList>
                    ) : (
                        <Message variant="body_short">No sensor types available.</Message>
                    )}
                </WideCard>

                <ChartCard
                    title="Status"
                    subtitle="All sensors"
                    segments={statusSegments}
                    onSegmentClick={handleStatusSegmentClick}
                />
                <ChartCard
                    title="Battery level"
                    subtitle="Active & offline sensors"
                    segments={batterySegments}
                    compactLegend
                    onSegmentClick={handleBatterySegmentClick}
                />
                <ChartCard
                    title="Signal strength"
                    subtitle="Active & offline sensors"
                    segments={signalSegments}
                    compactLegend
                    onSegmentClick={handleSignalSegmentClick}
                />
            </CardGrid>
        </PageContainer>
    )
}

export default PlantStatus
